<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useUser, useAuth, OrganizationSwitcher } from '@clerk/vue';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';
import { alertsApi } from '@/api/alerts';
import { billingApi } from '@/api/billing';
import { setLocale, getLocale } from '@/i18n';

const router = useRouter();
const appStore = useAppStore();
const { user } = useUser();
const { signOut } = useAuth();
const { t } = useI18n();

const locale = ref(getLocale());

function switchLocale(l: 'zh' | 'en') {
  locale.value = l;
  setLocale(l);
}

const menuItems = computed(() => [
  { key: 'nav.dashboard', path: '/dashboard' },
  { key: 'nav.campaigns', path: '/campaigns' },
  { key: 'nav.landingPages', path: '/landing-pages' },
  { key: 'nav.offers', path: '/offers' },
  { key: 'nav.conversions', path: '/conversions' },
  { key: 'nav.postbackLogs', path: '/postback-logs' },
  { key: 'nav.reports', path: '/reports' },
  { key: 'nav.trafficSources', path: '/traffic-sources' },
  { key: 'nav.clicks', path: '/clicks' },
  { key: 'nav.alerts', path: '/alerts' },
  { key: 'nav.billing', path: '/billing' },
  { key: 'nav.testing', path: '/testing' },
]);

const unreadCount = ref(0);
const isAdmin = ref(false);
const currentPath = computed(() => router.currentRoute.value.path);
const userInitial = computed(() => {
  const name = user.value?.fullName || user.value?.username || 'U';
  return name.charAt(0).toUpperCase();
});
const displayName = computed(() => user.value?.fullName || user.value?.username || '');

async function handleLogout() {
  await signOut.value();
  router.push('/');
}

onMounted(async () => {
  try {
    const notifications = await alertsApi.getNotifications(true);
    unreadCount.value = notifications.length;
  } catch {}
  try {
    await billingApi.adminListConfigs();
    isAdmin.value = true;
  } catch {}
});
</script>

<template>
  <div class="flex h-screen bg-gray-100">
    <aside :class="['flex flex-col border-r border-gray-200 bg-white transition-all duration-300', appStore.sidebarCollapsed ? 'w-16' : 'w-64']">
      <div class="flex h-16 items-center justify-center border-b border-gray-200">
        <router-link to="/" class="flex items-center">
          <span v-if="!appStore.sidebarCollapsed" class="text-xl font-bold text-blue-600">LiteTrack</span>
          <span v-else class="text-xl font-bold text-blue-600">L</span>
        </router-link>
      </div>

      <nav class="flex-1 space-y-1 p-2">
        <router-link
          v-for="item in menuItems" :key="item.path" :to="item.path"
          :class="['flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors', currentPath === item.path ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900']"
        >
          <svg class="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span v-if="!appStore.sidebarCollapsed" class="ml-3">{{ t(item.key) }}</span>
        </router-link>
      </nav>

      <div class="border-t border-gray-200 p-4">
        <button @click="appStore.toggleSidebar" class="flex w-full items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <svg :class="['h-5 w-5 transition-transform', appStore.sidebarCollapsed && 'rotate-180']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </div>
    </aside>

    <div class="flex flex-1 flex-col overflow-hidden">
      <header class="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
        <div />

        <div class="flex items-center space-x-3">
          <!-- Organization Switcher -->
          <OrganizationSwitcher />

          <!-- Admin entry (admin only) -->
          <button
            v-if="isAdmin"
            @click="router.push('/admin/billing')"
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {{ t('nav.adminPanel') }}
          </button>

          <!-- Language switcher -->
          <div class="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button @click="switchLocale('zh')" :class="['px-2 py-0.5 text-xs font-medium rounded transition-all', locale === 'zh' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700']">中</button>
            <button @click="switchLocale('en')" :class="['px-2 py-0.5 text-xs font-medium rounded transition-all', locale === 'en' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700']">EN</button>
          </div>

          <!-- Alerts bell -->
          <button @click="router.push('/alerts')" class="relative rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span v-if="unreadCount > 0" class="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />
          </button>

          <div class="flex items-center space-x-2">
            <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <span class="text-sm font-medium text-blue-700">{{ userInitial }}</span>
            </div>
            <span class="text-sm font-medium text-gray-700">{{ displayName }}</span>
          </div>

          <button @click="handleLogout" class="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900">
            {{ t('common.logout') }}
          </button>
        </div>
      </header>

      <main class="flex-1 overflow-auto p-6">
        <slot />
      </main>
    </div>
  </div>
</template>
